import { ENCRYPTION_CONTENT_HEADER, IFRAME_RESIZE_MESSAGE } from 'constants';
import doesStartWithContentHeader from 'utilities/encryptionUtilities/doesStartWithContentHeader';
import decryptContentString from 'utilities/encryptionUtilities/decryptContentString';
import stripSubresources from 'utilities/documentUtilities/stripSubresources';
import replaceBodyHTML from 'utilities/documentUtilities/replaceBodyHTML';
import addScript from 'utilities/documentUtilities/addScript';
import addMathJax from 'utilities/documentUtilities/addMathJax';

/**
 * Performs all of the modifications that we need to make to publisher html content.
 * This is done one time, as soon as it's loaded into an iframe. Includes decryption.
 *
 * @method prepareDocumentHTML
 * @param {Element} frame - iframe element
 * @param {Object} options - object with configuration options
 */
const prepareDocumentHTML = async (
  frame,
  options = {
    shouldAddMathJax: true,
    shouldStripSubresources: false,
  },
) => {
  const frameDocument = frame.contentDocument;
  const frameWindow = frame.contentWindow;

  const encryptionIndicatedByContentHeader = doesStartWithContentHeader(
    frameDocument.body.textContent,
    ENCRYPTION_CONTENT_HEADER,
  );

  // Handle decryption
  if (encryptionIndicatedByContentHeader) {
    // Remove scripts inserted by browser extensions and whitespace before decrypting
    const encryptedContent =
      (await frameDocument.body?.innerHTML.split('<script')[0].trim()) || '';

    let decryptedHTML = await decryptContentString(encryptedContent);

    // Strip subresources if the option is set
    if (options.shouldStripSubresources) {
      decryptedHTML = stripSubresources(decryptedHTML);
    }

    // Ensure that all elements with the epub:type attribute (should just be the pagebreaks) have the epub namespace, in case it is lost on the parent document.
    decryptedHTML = decryptedHTML.replace(
      /epub:type/g,
      'xmlns:epub="http://www.idpf.org/2007/ops" epub:type',
    );

    replaceBodyHTML(frameDocument, decryptedHTML);
  }

  // Remove legacy "content positioning layer" (CPL) files that were hard-coded into the manifest xhtml.
  // The CPL was a RedShelf system for supporting PDFs in the V6 eReader.
  [
    frameDocument.head?.querySelector(
      'link[href^="/static/reader/rs-cpl-base"]',
    ),
    frameDocument.head?.querySelector(
      'script[src^="/static/reader/rs-cpl-base"]',
    ),
  ].forEach((element) => element?.parentElement.removeChild(element));

  // Set scrolling to "auto" on all nested iframes in the publisher content.
  // This mimics functionality present in the old "rs-cpl-base.js"
  frameDocument.querySelectorAll('iframe').forEach((iframe) => {
    iframe.setAttribute('scrolling', 'auto');
  });

  // Inject a script into the iframe that alerts the reader whenever the content size changes
  addScript(
    frameDocument,
    null,
    `
window.ResizeObserver = window.ResizeObserver || function ResizeObserver() {
  console.error('ResizeObserver is not supported in this browser.');
  return {
    observe() {},
    unobserve() {},
    disconnect() {},
  };
}
const RedShelfResizeObserver = new ResizeObserver(entries => {
  window.parent.postMessage('${IFRAME_RESIZE_MESSAGE}');
});
RedShelfResizeObserver.observe(document.documentElement);
RedShelfResizeObserver.observe(document.body);
if (window.RedshelfWebView) {
  document.documentElement.style.visibility = 'hidden';
}`,
  );

  if (options.shouldAddMathJax) {
    // if no book record included in location data passed to component, assume not pdf
    // Add MathJax
    await addMathJax(frameWindow);
  }

  frameDocument.body
    ?.querySelectorAll('[*|type="pagebreak"]')
    .forEach((pagebreak) => pagebreak.setAttribute('aria-hidden', 'true'));
};

export default prepareDocumentHTML;
